import {createSlice, createAsyncThunk} from '@reduxjs/toolkit'
import {AuthCheck, DownloadFileFn} from '../../helpers'
import {
  SearchProductClient,
  GetProductClient,
  GetProductInfoClient,
  DeleteProductClient,
  UpdateProductClient,
  CreateProductClient,
  CreateFastProductClient,
  ArchivingProductClient,
  UpdateWarehousesProductClient,
  GetStockProductClient,
  SearchExpenseInvoiceProductClient,
  SearchSaleInvoiceProductClient,
  SearchStockProductClient,
  SearchWarehouseProductClient,
  SearchWarehouseClient,
  GetWarehouseClient,
  DeleteWarehouseClient,
  UpdateWarehouseClient,
  CreateWarehouseClient,
  ArchivingWarehouseClient,
  SetDefaultWarehouseClient,
  SearchOutgoingWaybillClient,
  GetOutgoingWaybillClient,
  DeleteOutgoingWaybillClient,
  UpdateOutgoingWaybillClient,
  CreateOutgoingWaybillClient,
  SearchIncomingWaybillClient,
  GetIncomingWaybillClient,
  DeleteIncomingWaybillClient,
  UpdateIncomingWaybillClient,
  CreateIncomingWaybillClient,
  SearchWarehouseTransferClient,
  GetWarehouseTransferClient,
  DeleteWarehouseTransferClient,
  CreateWarehouseTransferClient,
} from '../../api'

const SERVICE_URL = window.ENVVARS.REACT_APP_SERVICE_URL

// API Clients
const searchProductClient = new SearchProductClient(SERVICE_URL)
const getProductClient = new GetProductClient(SERVICE_URL)
const getProductInfoClient = new GetProductInfoClient(SERVICE_URL)
const deleteProductClient = new DeleteProductClient(SERVICE_URL)
const updateProductClient = new UpdateProductClient(SERVICE_URL)
const createProductClient = new CreateProductClient(SERVICE_URL)
const createFastProductClient = new CreateFastProductClient(SERVICE_URL)
const archivingProductClient = new ArchivingProductClient(SERVICE_URL)
const updateWarehousesProductClient = new UpdateWarehousesProductClient(SERVICE_URL)
const getStockProductClient = new GetStockProductClient(SERVICE_URL)
const searchExpenseInvoiceProductClient = new SearchExpenseInvoiceProductClient(SERVICE_URL)
const searchSaleInvoiceProductClient = new SearchSaleInvoiceProductClient(SERVICE_URL)
const searchStockProductClient = new SearchStockProductClient(SERVICE_URL)
const searchWarehouseProductClient = new SearchWarehouseProductClient(SERVICE_URL)

const searchWarehouseClient = new SearchWarehouseClient(SERVICE_URL)
const getWarehouseClient = new GetWarehouseClient(SERVICE_URL)
const deleteWarehouseClient = new DeleteWarehouseClient(SERVICE_URL)
const updateWarehouseClient = new UpdateWarehouseClient(SERVICE_URL)
const createWarehouseClient = new CreateWarehouseClient(SERVICE_URL)
const archivingWarehouseClient = new ArchivingWarehouseClient(SERVICE_URL)
const setDefaultWarehouseClient = new SetDefaultWarehouseClient(SERVICE_URL)

const searchOutgoingWaybillClient = new SearchOutgoingWaybillClient(SERVICE_URL)
const getOutgoingWaybillClient = new GetOutgoingWaybillClient(SERVICE_URL)
const deleteOutgoingWaybillClient = new DeleteOutgoingWaybillClient(SERVICE_URL)
const updateOutgoingWaybillClient = new UpdateOutgoingWaybillClient(SERVICE_URL)
const createOutgoingWaybillClient = new CreateOutgoingWaybillClient(SERVICE_URL)

const searchIncomingWaybillClient = new SearchIncomingWaybillClient(SERVICE_URL)
const getIncomingWaybillClient = new GetIncomingWaybillClient(SERVICE_URL)
const deleteIncomingWaybillClient = new DeleteIncomingWaybillClient(SERVICE_URL)
const updateIncomingWaybillClient = new UpdateIncomingWaybillClient(SERVICE_URL)
const createIncomingWaybillClient = new CreateIncomingWaybillClient(SERVICE_URL)

const searchWarehouseTransferClient = new SearchWarehouseTransferClient(SERVICE_URL)
const getWarehouseTransferClient = new GetWarehouseTransferClient(SERVICE_URL)
const deleteWarehouseTransferClient = new DeleteWarehouseTransferClient(SERVICE_URL)
const createWarehouseTransferClient = new CreateWarehouseTransferClient(SERVICE_URL)

const initialState = {
  loading: false,
  isRedirect: false,
  isRedirectPage: false,
  error: null,
  isDeleted: false,
  isArchived: false,
  isDefault: false,
  updateWareHouses: false,
  excel: null,
  product: null,
  detail: null,
  productStock: null,
  searchStock: null,
  productSaleInvoice: null,
  productExpenseInvoice: null,
  searchWarehouse: null,
  warehouse: null,
  outgoingWaybill: null,
  incomingWaybill: null,
  warehouseTransfer: null,
}

// Async Thunks
export const productGetAll = createAsyncThunk('product/getAll', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await searchProductClient.search(
      params.name,
      params.items_per_page,
      params.page,
      params.order,
      params.sort
    )
    dispatch(setProduct(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const productExcel = createAsyncThunk(
  'product/excel',
  async ({params, fileName}, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await searchProductClient.excel(
        params.name,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      if (response.data) {
        DownloadFileFn(response.data, fileName)
      }
      dispatch(setExcel(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const productGetId = createAsyncThunk('product/getId', async (id, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await getProductClient.get(id)
    dispatch(setDetail(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const productGetInfo = createAsyncThunk('product/getInfo', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await getProductInfoClient.get(params)
    dispatch(setDetail(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const productDelete = createAsyncThunk('product/delete', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await deleteProductClient.delete(params)
    dispatch(setDeleted(true))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const productArchiving = createAsyncThunk(
  'product/archiving',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await archivingProductClient.archive(params)
      dispatch(setArchived(true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const productCreate = createAsyncThunk('product/create', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await createProductClient.create(params)
    dispatch(setRedirect(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const productCreateFast = createAsyncThunk(
  'product/createFast',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await createFastProductClient.create(params)
      dispatch(setRedirect(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const productUpdate = createAsyncThunk('product/update', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await updateProductClient.update(params)
    dispatch(setRedirect(response || params || true))
    dispatch(setDetail(params))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setDetail(params))
    dispatch(setError(error))
    throw error
  }
})

export const productUpdateWarehouses = createAsyncThunk(
  'product/updateWarehouses',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await updateWarehousesProductClient.update(params)
      dispatch(setUpdateWareHouses(true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const productGetStock = createAsyncThunk('product/getStock', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await getStockProductClient.getAll(params)
    dispatch(setProductStock(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const productSearchExpenseInvoice = createAsyncThunk(
  'product/searchExpenseInvoice',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await searchExpenseInvoiceProductClient.search(params)
      dispatch(setProductExpenseInvoice(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const productSearchSaleInvoice = createAsyncThunk(
  'product/searchSaleInvoice',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await searchSaleInvoiceProductClient.search(params)
      dispatch(setProductSaleInvoice(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const productSearchStock = createAsyncThunk(
  'product/searchStock',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await searchStockProductClient.search(params)
      dispatch(setSearchStock(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const productSearchWarehouse = createAsyncThunk(
  'product/searchWarehouse',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await searchWarehouseProductClient.search(params)
      dispatch(setSearchWarehouse(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

// Warehouse Async Thunks
export const warehouseGetAll = createAsyncThunk('warehouse/getAll', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await searchWarehouseClient.search(
      params.name,
      params.items_per_page,
      params.page,
      params.order,
      params.sort
    )
    dispatch(setWarehouse(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const warehouseExcel = createAsyncThunk(
  'warehouse/excel',
  async ({params, fileName}, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await searchWarehouseClient.excel(
        params.name,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      if (response.data) {
        DownloadFileFn(response.data, fileName)
      }
      dispatch(setExcel(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const warehouseGetId = createAsyncThunk('warehouse/getId', async (id, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await getWarehouseClient.get(id)
    dispatch(setDetail(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const warehouseDelete = createAsyncThunk('warehouse/delete', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await deleteWarehouseClient.delete(params)
    dispatch(setDeleted(true))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const warehouseArchiving = createAsyncThunk(
  'warehouse/archiving',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await archivingWarehouseClient.archive(params)
      dispatch(setArchived(true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const warehouseCreate = createAsyncThunk('warehouse/create', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await createWarehouseClient.create(params)
    dispatch(setRedirect(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const warehouseUpdate = createAsyncThunk('warehouse/update', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await updateWarehouseClient.update(params)
    dispatch(setRedirect(response || params || true))
    dispatch(setDetail(params))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setDetail(params))
    dispatch(setError(error))
    throw error
  }
})

export const warehouseSetDefault = createAsyncThunk(
  'warehouse/setDefault',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await setDefaultWarehouseClient.update(params)
      dispatch(setDefault(true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

// OutgoingWaybill Async Thunks
export const outgoingWaybillGetAll = createAsyncThunk(
  'outgoingWaybill/getAll',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await searchOutgoingWaybillClient.search(
        params.name,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      dispatch(setOutgoingWaybill(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const outgoingWaybillExcel = createAsyncThunk(
  'outgoingWaybill/excel',
  async ({params, fileName}, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await searchOutgoingWaybillClient.excel(
        params.name,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      if (response.data) {
        DownloadFileFn(response.data, fileName)
      }
      dispatch(setExcel(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const outgoingWaybillGetId = createAsyncThunk(
  'outgoingWaybill/getId',
  async (id, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await getOutgoingWaybillClient.get(id)
      dispatch(setDetail(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const outgoingWaybillDelete = createAsyncThunk(
  'outgoingWaybill/delete',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await deleteOutgoingWaybillClient.delete(params)
      dispatch(setDeleted(true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const outgoingWaybillCreate = createAsyncThunk(
  'outgoingWaybill/create',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await createOutgoingWaybillClient.create(params)
      dispatch(setRedirect(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const outgoingWaybillUpdate = createAsyncThunk(
  'outgoingWaybill/update',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await updateOutgoingWaybillClient.update(params)
      dispatch(setRedirectPage(response || params))
      dispatch(setDetail(params))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setDetail(params))
      dispatch(setError(error))
      throw error
    }
  }
)

// IncomingWaybill Async Thunks
export const incomingWaybillGetAll = createAsyncThunk(
  'incomingWaybill/getAll',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await searchIncomingWaybillClient.search(
        params.name,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      dispatch(setIncomingWaybill(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const incomingWaybillExcel = createAsyncThunk(
  'incomingWaybill/excel',
  async ({params, fileName}, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await searchIncomingWaybillClient.excel(
        params.name,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      if (response.data) {
        DownloadFileFn(response.data, fileName)
      }
      dispatch(setExcel(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const incomingWaybillGetId = createAsyncThunk(
  'incomingWaybill/getId',
  async (id, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await getIncomingWaybillClient.get(id)
      dispatch(setDetail(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const incomingWaybillDelete = createAsyncThunk(
  'incomingWaybill/delete',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await deleteIncomingWaybillClient.delete(params)
      dispatch(setDeleted(true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const incomingWaybillCreate = createAsyncThunk(
  'incomingWaybill/create',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await createIncomingWaybillClient.create(params)
      dispatch(setRedirect(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const incomingWaybillUpdate = createAsyncThunk(
  'incomingWaybill/update',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await updateIncomingWaybillClient.update(params)
      dispatch(setRedirect(response || params || true))
      dispatch(setDetail(params))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setDetail(params))
      dispatch(setError(error))
      throw error
    }
  }
)

// WarehouseTransfer Async Thunks
export const warehouseTransferGetAll = createAsyncThunk(
  'warehouseTransfer/getAll',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await searchWarehouseTransferClient.search(
        params.name,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      dispatch(setWarehouseTransfer(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const warehouseTransferExcel = createAsyncThunk(
  'warehouseTransfer/excel',
  async ({params, fileName}, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await searchWarehouseTransferClient.excel(
        params.name,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      if (response.data) {
        DownloadFileFn(response.data, fileName)
      }
      dispatch(setExcel(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const warehouseTransferGetId = createAsyncThunk(
  'warehouseTransfer/getId',
  async (id, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await getWarehouseTransferClient.get(id)
      dispatch(setDetail(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const warehouseTransferDelete = createAsyncThunk(
  'warehouseTransfer/delete',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await deleteWarehouseTransferClient.delete(params)
      dispatch(setDeleted(true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const warehouseTransferCreate = createAsyncThunk(
  'warehouseTransfer/create',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await createWarehouseTransferClient.create(params)
      dispatch(setRedirect(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

// Slice
const productSlice = createSlice({
  name: 'product',
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload
    },
    setError: (state, action) => {
      state.error = action.payload
      state.loading = false
    },
    setProduct: (state, action) => {
      state.product = action.payload
      state.loading = false
    },
    setDeleted: (state, action) => {
      state.isDeleted = action.payload
      state.loading = false
    },
    setRedirectPage: (state, action) => {
      state.isRedirectPage = action.payload
      state.loading = false
    },
    setRedirect: (state, action) => {
      state.isRedirect = action.payload
      state.loading = false
    },
    setDetail: (state, action) => {
      state.detail = action.payload
      state.loading = false
    },
    setArchived: (state, action) => {
      state.isArchived = action.payload
      state.loading = false
    },
    setDefault: (state, action) => {
      state.isDefault = action.payload
      state.loading = false
    },
    setUpdateWareHouses: (state, action) => {
      state.updateWareHouses = action.payload
      state.loading = false
    },
    setProductStock: (state, action) => {
      state.productStock = action.payload
      state.loading = false
    },
    setSearchStock: (state, action) => {
      state.searchStock = action.payload
      state.loading = false
    },
    setProductSaleInvoice: (state, action) => {
      state.productSaleInvoice = action.payload
      state.loading = false
    },
    setProductExpenseInvoice: (state, action) => {
      state.productExpenseInvoice = action.payload
      state.loading = false
    },
    setSearchWarehouse: (state, action) => {
      state.searchWarehouse = action.payload
      state.loading = false
    },
    setWarehouse: (state, action) => {
      state.warehouse = action.payload
      state.loading = false
    },
    setOutgoingWaybill: (state, action) => {
      state.outgoingWaybill = action.payload
      state.loading = false
    },
    setIncomingWaybill: (state, action) => {
      state.incomingWaybill = action.payload
      state.loading = false
    },
    setWarehouseTransfer: (state, action) => {
      state.warehouseTransfer = action.payload
      state.loading = false
    },
    setExcel: (state, action) => {
      state.excel = action.payload
      state.loading = false
    },
    resetState: (state) => {
      Object.assign(state, initialState)
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (action) => action.type.startsWith('product/') && action.type.endsWith('/pending'),
      (state) => {
        state.loading = true
      }
    )
  }
})

// Export actions
export const {
  setLoading,
  setError,
  setProduct,
  setDeleted,
  setRedirectPage,
  setRedirect,
  setDetail,
  setArchived,
  setDefault,
  setUpdateWareHouses,
  setProductStock,
  setSearchStock,
  setProductSaleInvoice,
  setProductExpenseInvoice,
  setSearchWarehouse,
  setWarehouse,
  setOutgoingWaybill,
  setIncomingWaybill,
  setWarehouseTransfer,
  setExcel,
  resetState,
} = productSlice.actions

// Selectors
export const selectProductLoading = (state) => state.product.loading
export const selectProductError = (state) => state.product.error
export const selectProduct = (state) => state.product.product
export const selectProductDeleted = (state) => state.product.isDeleted
export const selectProductRedirectPage = (state) => state.product.isRedirectPage
export const selectProductRedirect = (state) => state.product.isRedirect
export const selectProductDetail = (state) => state.product.detail
export const selectProductArchived = (state) => state.product.isArchived
export const selectProductDefault = (state) => state.product.isDefault
export const selectProductUpdateWareHouses = (state) => state.product.updateWareHouses
export const selectProductStock = (state) => state.product.productStock
export const selectProductSearchStock = (state) => state.product.searchStock
export const selectProductSaleInvoice = (state) => state.product.productSaleInvoice
export const selectProductExpenseInvoice = (state) => state.product.productExpenseInvoice
export const selectProductSearchWarehouse = (state) => state.product.searchWarehouse
export const selectProductWarehouse = (state) => state.product.warehouse
export const selectProductOutgoingWaybill = (state) => state.product.outgoingWaybill
export const selectProductIncomingWaybill = (state) => state.product.incomingWaybill
export const selectProductWarehouseTransfer = (state) => state.product.warehouseTransfer
export const selectProductExcel = (state) => state.product.excel

export default productSlice.reducer
